// components
import { InputAdornment, TextField } from '@mui/material';
import { DesktopTimePicker } from '@mui/x-date-pickers-pro';
import DatePicker from '../DatePicker/DatePicker';
import { GridCompact4, GridCompact8 } from '../Grid/Grid.components';

// styles
import {
  CompactGridContainer,
  StyledCustomTimePickerField,
  StyledInputAdornmentTypography
} from './PeriodInput.styles';

// react/utils
import { useTranslation } from 'react-i18next';
import { usePeriodInputController } from './hooks/usePeriodInputController';
import { toDate } from './PeriodInput.utils';

// types
import type { InputAdornmentProps } from '@mui/material';
import type { PeriodInputProps } from './PeriodInput.types';

const PeriodInput = ({
  label,
  period,
  error,
  clearable = true,
  sameDayOnly,
  minDate,
  onChange
}: PeriodInputProps) => {
  'use memo';

  const { t } = useTranslation('common');

  const {
    startDate,
    startTime,
    durationHours,
    durationMinutes,
    fullEnd,
    minEndTime,
    maxEndTime,
    overflowDays,
    handleDateChange,
    handleStartTimeChange,
    handleEndTimeChange,
    handleHoursChange,
    handleMinutesChange
  } = usePeriodInputController({ period, sameDayOnly, onChange });

  const CustomTimeOverflowInputAdornment = ({
    children,
    ...other
  }: InputAdornmentProps) => {
    return (
      <InputAdornment {...other}>
        <StyledInputAdornmentTypography variant="body1">
          {overflowDays > 0 && `+${overflowDays}`}
        </StyledInputAdornmentTypography>
        {children}
      </InputAdornment>
    );
  };

  return (
    <CompactGridContainer>
      <GridCompact8>
        <DatePicker
          minDate={minDate}
          date={startDate}
          label={label || t('startDate')}
          onChange={handleDateChange}
          clearable={clearable}
          size="small"
        />
      </GridCompact8>
      <GridCompact4>
        <DesktopTimePicker
          ampm={false}
          value={startTime || null}
          onChange={(value, _ctx) => handleStartTimeChange(toDate(value))}
          label={t('from')}
          views={['hours', 'minutes']}
          enableAccessibleFieldDOMStructure={false}
          slots={{
            textField: StyledCustomTimePickerField
          }}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: 'outlined',
              size: 'small'
            },
            openPickerButton: {
              tabIndex: -1
            }
          }}
        />
      </GridCompact4>
      <GridCompact4>
        <TextField
          value={durationHours}
          label={t('hours')}
          variant="outlined"
          fullWidth
          size="small"
          inputMode="numeric"
          slotProps={{
            htmlInput: {
              min: 0,
              max: 23,
              pattern: '[0-9]*',
              inputMode: 'numeric'
            }
          }}
          onChange={handleHoursChange}
        />
      </GridCompact4>
      <GridCompact4>
        <TextField
          value={durationMinutes}
          label={t('minutes')}
          variant="outlined"
          fullWidth
          size="small"
          slotProps={{
            htmlInput: {
              inputMode: 'numeric',
              pattern: '[0-9]*',
              min: 0,
              max: 59
            }
          }}
          onChange={handleMinutesChange}
        />
      </GridCompact4>
      <GridCompact4>
        <DesktopTimePicker
          ampm={false}
          value={fullEnd || null}
          minTime={minEndTime || undefined}
          maxTime={maxEndTime || undefined}
          label={t('until')}
          onChange={(value, _ctx) => handleEndTimeChange(toDate(value))}
          enableAccessibleFieldDOMStructure={false}
          slotProps={{
            textField: {
              fullWidth: true,
              variant: 'outlined',
              size: 'small',
              error: !!error,
              helperText: error
            },
            openPickerButton: {
              tabIndex: -1
            }
          }}
          slots={{
            inputAdornment: CustomTimeOverflowInputAdornment,
            textField: StyledCustomTimePickerField
          }}
        />
      </GridCompact4>
    </CompactGridContainer>
  );
};

export default PeriodInput;
